import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import Services from '../services/Services';
import { UserProfile } from '../types/profile.types';
import { Channel } from '../types/channel.types';
import { registerAbortController, unregisterAbortController, isLoggingOut } from '../utils/requestCancellation';
import { useGeneral } from '../contexts';

interface UseUserProfileOptions {
  token: string | null;
  userID: string | null;
  isSelf: boolean;
  loadingKey?: string;
}

export const useUserProfile = (options: UseUserProfileOptions) => {
  const { token, userID, isSelf, loadingKey } = options;
  const { setLoading, toastSuccess, toastError, language } = useGeneral();
  const { getTranslation } = language;
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [isFollowing, setIsFollowing] = useState(false);
  const [followLoading, setFollowLoading] = useState(false);
  const [localLoading, setLocalLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refetchCounter, setRefetchCounter] = useState(0);
  const isMountedRef = useRef(true);

  const loading = localLoading;

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const abortController = new AbortController();
    registerAbortController(abortController);

    const fetchProfile = async () => {
      if (!userID || !token || isLoggingOut()) {
        if (isMountedRef.current) {
          setLocalLoading(false);
          if (loadingKey) setLoading(loadingKey, false);
        }
        return;
      }

      try {
        if (isMountedRef.current) {
          setLocalLoading(true);
          if (loadingKey) setLoading(loadingKey, true);
          setError(null);
        }

        // Fetch profile and follow status in parallel (only check follow status for other users)
        const promises: Promise<any>[] = [
          Services.GetAnyUserInfos(token, userID, abortController)
        ];

        if (!isSelf) {
          promises.push(Services.SocialHiveAmIFollowingUser(token, userID));
        }

        const results = await Promise.all(promises);

        if (abortController.signal.aborted || !isMountedRef.current) {
          return;
        }

        const profileResponse = results[0];
        if (profileResponse.data.status && profileResponse.data.result) {
          setProfile(profileResponse.data.result as UserProfile);
        } else {
          setError(profileResponse.data.message || getTranslation('profile.profileNotFound'));
        }

        if (!isSelf && results[1]) {
          const followResponse = results[1];
          if (followResponse.data.status) {
            setIsFollowing(!!followResponse.data.result);
          }
        }
      } catch (err: any) {
        if (err.name === 'AbortError' || err.name === 'CanceledError' || err.message?.includes('Logout in progress')) {
          return;
        }

        if (isMountedRef.current) {
          setError(err.message || 'An error occurred');
        }
      } finally {
        if (isMountedRef.current && !abortController.signal.aborted) {
          setLocalLoading(false);
          if (loadingKey) setLoading(loadingKey, false);
        } else if (loadingKey) {
          setLoading(loadingKey, false);
        }
      }
    };

    fetchProfile();

    return () => {
      abortController.abort();
      unregisterAbortController(abortController);
      if (loadingKey) {
        setLoading(loadingKey, false);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, userID, isSelf, loadingKey, setLoading, getTranslation, refetchCounter]);

  const refetchProfile = useCallback(() => {
    setRefetchCounter(c => c + 1);
  }, []);

  const followUser = useCallback(async () => {
    if (!token || !userID || followLoading) return;

    setFollowLoading(true);
    try {
      const response = await Services.SocialHiveFollowUser(token, userID);
      if (response.data.status) {
        setIsFollowing(true);
        setProfile(prev => prev ? {
          ...prev,
          followerCount: prev.followerCount + 1
        } : null);
        toastSuccess(getTranslation('profile.followSuccess'));
      } else {
        toastError(getTranslation('profile.followError'));
      }
    } catch {
      toastError(getTranslation('profile.followError'));
    } finally {
      setFollowLoading(false);
    }
  }, [token, userID, followLoading, toastSuccess, toastError, getTranslation]);

  const unfollowUser = useCallback(async () => {
    if (!token || !userID || followLoading) return;

    setFollowLoading(true);
    try {
      const response = await Services.SocialHiveUnfollowUser(token, userID);
      if (response.data.status) {
        setIsFollowing(false);
        setProfile(prev => prev ? {
          ...prev,
          followerCount: Math.max(0, prev.followerCount - 1)
        } : null);
        toastSuccess(getTranslation('profile.unfollowSuccess'));
      } else {
        toastError(getTranslation('profile.unfollowError'));
      }
    } catch {
      toastError(getTranslation('profile.unfollowError'));
    } finally {
      setFollowLoading(false);
    }
  }, [token, userID, followLoading, toastSuccess, toastError, getTranslation]);

  // Sort events newest to oldest
  const sortedEvents = useMemo(() => {
    if (!profile?.registeredEvents || profile.registeredEvents.length === 0) return [];
    return [...profile.registeredEvents].sort(
      (a, b) => (Number(b.eventStartDateTime) || 0) - (Number(a.eventStartDateTime) || 0)
    );
  }, [profile]);

  const communities = useMemo((): Channel[] => {
    return profile?.joinnedChannels || [];
  }, [profile]);

  const stats = useMemo(() => ({
    eventsCount: profile?.registeredEvents?.length || 0,
    communitiesCount: profile?.joinnedChannels?.length || 0,
    followerCount: profile?.followerCount || 0,
    followingCount: profile?.followingCount || 0,
  }), [profile]);

  return {
    profile,
    events: sortedEvents,
    communities,
    stats,
    isFollowing,
    followLoading,
    loading,
    error,
    followUser,
    unfollowUser,
    refetchProfile,
  };
};
